import {
  Alert,
  Button,
  FieldError,
  Input,
  Label,
  TextField,
  Typography,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useMemo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { VariantAttributeFormData } from "./schema";

export const OptionsEditor = () => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<VariantAttributeFormData>();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "options",
  });

  const watchedOptions = watch("options");
  const visibleFields = fields.filter((f) => !f.delete);

  const duplicateNames = useMemo(() => {
    const visibleOptions = watchedOptions?.filter((o) => !o.delete) || [];
    const names = visibleOptions
      .map((o) => o.name?.trim().toLowerCase())
      .filter(Boolean);
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (const name of names) {
      if (seen.has(name)) {
        duplicates.add(name);
      }
      seen.add(name);
    }

    return duplicates;
  }, [watchedOptions]);

  const addOption = () => {
    append({ name: "", value: "" });
  };

  const removeOption = (index: number) => {
    const option = fields[index];

    if (visibleFields.length <= 1) return;

    if (option.optionId) {
      update(index, { ...option, delete: true });
    } else {
      remove(index);
    }
  };

  const optionsError = errors.options;
  const hasArrayError =
    optionsError && "message" in optionsError && optionsError.message;

  return (
    <div className="flex flex-col gap-3">
      <Label>Options</Label>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 text-xs text-muted">
          <Typography className="flex-1" type="body-sm" weight="medium">
            Display Name
          </Typography>
          <Typography className="flex-1" type="body-sm" weight="medium">
            Value
          </Typography>
          <span className="w-8" />
        </div>
        {fields.map((option, index) => {
          if (option.delete) return null;

          return (
            <div key={option.id} className="flex gap-2 items-start">
              <Controller
                control={control}
                name={`options.${index}.name`}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <TextField
                    className="flex-1"
                    isInvalid={Boolean(fieldError?.message)}
                    name={field.name}
                    value={field.value ?? ""}
                    variant="secondary"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  >
                    <Input ref={field.ref} placeholder="e.g., Small" />
                    {fieldError?.message ? (
                      <FieldError>{fieldError.message}</FieldError>
                    ) : null}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name={`options.${index}.value`}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <TextField
                    className="flex-1"
                    isInvalid={Boolean(fieldError?.message)}
                    name={field.name}
                    value={field.value ?? ""}
                    variant="secondary"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  >
                    <Input ref={field.ref} placeholder="e.g., S" />
                    {fieldError?.message ? (
                      <FieldError>{fieldError.message}</FieldError>
                    ) : null}
                  </TextField>
                )}
              />
              <Button
                isIconOnly
                aria-label="Remove option"
                isDisabled={visibleFields.length <= 1}
                size="sm"
                variant="danger-soft"
                onPress={() => removeOption(index)}
              >
                <Icon fontSize={16} icon="solar:trash-bin-trash-linear" />
              </Button>
            </div>
          );
        })}
      </div>
      <Button
        className="self-start"
        size="sm"
        variant="tertiary"
        onPress={addOption}
      >
        <Icon fontSize={16} icon="solar:add-circle-linear" />
        Add Option
      </Button>
      {hasArrayError && (
        <Typography className="text-danger text-xs">
          {optionsError.message}
        </Typography>
      )}
      {duplicateNames.size > 0 && (
        <Alert status="warning">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>
              Duplicate option names: {Array.from(duplicateNames).join(", ")}
            </Alert.Description>
          </Alert.Content>
        </Alert>
      )}
    </div>
  );
};
